import type { CallFailure } from './api.js';
import { idbOpener, idbRequest, idbTransaction } from './idb.js';
import type { PendingOp } from './pending-ops.js';

/**
 * The read cache — offline plan v1.3 §2.2 C, increment 4.
 *
 * WHAT IS CACHED, and nothing else: the item list's first page, item details that have
 * actually been visited, and today's bookings. §2.2 C names exactly those three because
 * they are what somebody in a stockroom needs to answer "is this the right garment and
 * can I take it?". Everything else stays online-only and says so — which the existing
 * screens already do, since a failed read sets a visible problem rather than rendering
 * empty.
 *
 * A CACHE HIT IS LABELLED, NEVER SILENT. `cachedAt` travels with the value so a screen can
 * say "saved copy from 14:32" instead of implying it is live. An offline app that shows
 * yesterday's answer as though it were today's is worse than one that admits it is stale;
 * this is the same G10 reasoning as the queue badge.
 */

export interface CachedRead<T> {
  value: T;
  /** Epoch ms — an INSTANT, so no date helper applies (see shared/time/date.ts). */
  cachedAt: number;
}

export interface ReadCache {
  get<T>(key: string): Promise<CachedRead<T> | undefined>;
  put<T>(key: string, value: T): Promise<void>;
  /** Disposable by design: nothing here is a source of truth. */
  clear(): Promise<void>;
}

/** Cache keys, in one place so a screen and its cache cannot disagree about spelling. */
export const cacheKey = {
  itemListFirstPage: 'listItems:page1',
  item: (id: string) => `getItem:${id}`,
  /** Keyed by the atelier's calendar day, so yesterday's answer is a different entry
   * rather than an overwrite — `todayISO()` is what derives it at the call site. */
  bookingsToday: (isoDate: string) => `bookings:today:${isoDate}`,
} as const;

export function createMemoryReadCache(): ReadCache {
  const entries = new Map<string, CachedRead<unknown>>();
  return {
    get<T>(key: string) {
      return Promise.resolve(entries.get(key) as CachedRead<T> | undefined);
    },
    put<T>(key: string, value: T) {
      entries.set(key, { value, cachedAt: Date.now() });
      return Promise.resolve();
    },
    clear() {
      entries.clear();
      return Promise.resolve();
    },
  };
}

/**
 * ---------------------------------------------------------------------------
 * THE MERGE RULE, and an honest account of what it can and cannot do yet
 * ---------------------------------------------------------------------------
 * The standing rule is: *any cache primed from server reads MERGES pending queue items
 * (server ∪ pending-inserts − pending-deletes)*.
 *
 * TWO OF THOSE THREE ARMS HAVE NO PRODUCER TODAY, and pretending otherwise would be the
 * more dangerous kind of completeness. Nothing the queue can hold is an item INSERT —
 * intake-create is increment 3's unfinished remainder — and nothing deletable is
 * queueable at all, because bulk destructive operations are on the never-queue list by
 * §2.2 D. What the queue actually holds is MODIFICATIONS: a movement that will change an
 * item's status, and a photo that will attach to it.
 *
 * So the implemented arm is modification, and the insert arm arrives with intake-create.
 * `pendingInsertsUnsupported` below exists to make that a compile-time fact rather than a
 * comment somebody has to find.
 *
 * WHY THE MERGE DOES NOT REWRITE `status`, which is the decision worth arguing with:
 * a pending check-OUT has a deterministic outcome (the server permits it only from
 * `available` and always lands on `checked_out`), so it COULD be predicted. A pending
 * check-IN cannot — the resulting status is `available` or `flagged` depending on whether
 * the item has open blocking flags, which is a server-side question the device cannot
 * answer offline. Predicting one and not the other would make the same badge mean
 * "certain" in one place and "guess" in another. So neither is predicted: the item is
 * marked as HAVING a pending change, named, and the screen says so. That is G9 —
 * a default that looks like a decision is the thing to avoid — and it costs nothing,
 * because "someone has just checked this out and it has not reached the server" is
 * more useful to a person holding the garment than a status field that might be wrong.
 */

export type PendingChange = 'checking-out' | 'checking-in' | 'adding-photo' | 'raising-flag';

/** What the queue is about to do to this item, oldest intent first. */
export function pendingChangesFor(itemId: string, ops: PendingOp[]): PendingChange[] {
  return ops
    .sort((a, b) => a.createdAt - b.createdAt)
    .flatMap((op): PendingChange[] => {
      // An INTAKE has no `itemId` — it is the item, and it has no server identity until
      // it lands. It belongs to the list's insert arm below, never to a per-item view.
      if (op.kind === 'intake') return [];
      if (op.payload.itemId !== itemId) return [];
      if (op.kind === 'movement') return [op.payload.direction === 'out' ? 'checking-out' : 'checking-in'];
      if (op.kind === 'flag') return ['raising-flag'];
      return ['adding-photo'];
    });
}

/**
 * THE MERGE RULE'S `∪ pending-inserts` ARM, live as of increment 3's remainder.
 *
 * Increment 4 shipped this arm unimplemented and said so, because nothing the queue could
 * hold was an item insert. `intake` is that producer, so the cached list must now show
 * items captured on this device that the server has never heard of — otherwise somebody
 * catalogues six garments in a basement, reopens the app, and sees none of them.
 *
 * They are NOT merged into the server's rows and cannot be: a pending intake has no id,
 * no status and no photo key, so pretending it is an ordinary row would put something in
 * the list that cannot be opened. It is returned separately for the screen to render as
 * what it is — captured here, not yet sent.
 *
 * `− pending-deletes` still has no producer, and still cannot: bulk destructive operations
 * are on §2.2 D's never-queue list by design.
 */
export interface PendingIntake {
  opId: string;
  name: string;
  createdAt: number;
  photoCount: number;
}

export function pendingIntakes(ops: PendingOp[]): PendingIntake[] {
  return ops
    .filter((op): op is Extract<PendingOp, { kind: 'intake' }> => op.kind === 'intake')
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((op) => ({
      opId: op.id,
      name: op.payload.name,
      createdAt: op.createdAt,
      photoCount: op.blobs.length,
    }));
}

/**
 * Ops that are still going to happen. An `expired` or `failed` op is NOT a pending change
 * — it is a problem the badge is already reporting, and folding it in here would tell a
 * user their check-out is on its way when it has stopped.
 */
export function livePendingOps(ops: PendingOp[]): PendingOp[] {
  return ops.filter((op) => op.status === 'queued' || op.status === 'sending');
}

/**
 * The marker increment 4 left here — "when intake-create becomes queueable, the merge must
 * union locally-created items into a cached list" — is now `pendingIntakes` above, and the
 * list screen renders them. Removed rather than left lying: a marker for work that is done
 * is worse than no marker, because the next reader trusts it.
 *
 * What remains genuinely unimplemented is `− pending-deletes`, and it has no producer by
 * DESIGN rather than by omission: everything destructive is on §2.2 D's never-queue list.
 */
export const pendingDeletesUnsupportedByDesign = true as const;

export const CACHE_DB_NAME = 'mononoke-cache';
export const CACHE_STORE_NAME = 'reads';
const CACHE_DB_VERSION = 1;

/**
 * A SEPARATE DATABASE FROM THE QUEUE, and that is the decision rather than an accident.
 *
 * Putting the cache in `mononoke-ops` would have meant bumping the version of the database
 * that holds unsent captures. An upgrade path that goes wrong there loses work somebody
 * cannot retake; going wrong here loses a copy of something the server still has. Those
 * are not the same risk, so they do not share a schema version.
 *
 * (It buys nothing against eviction — IndexedDB pressure is per-origin — and this comment
 * exists so nobody later assumes it does.)
 */
export function createIndexedDbReadCache(dbName: string = CACHE_DB_NAME): ReadCache {
  const open = idbOpener(dbName, CACHE_DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
      db.createObjectStore(CACHE_STORE_NAME);
    }
  });

  /**
   * EVERY FAILURE HERE IS A MISS, NOT A THROW — the opposite of the queue's rule, and for
   * the opposite reason. An unreadable QUEUE must be refused loudly, because reporting
   * "nothing pending" would hide captured work. An unreadable CACHE is just an absent
   * copy: the caller falls through to the network, which is what it would have done
   * anyway. Making a broken cache take the screen down with it would be the cure being
   * worse than the disease.
   */
  return {
    async get<T>(key: string) {
      try {
        const db = await open();
        const tx = db.transaction(CACHE_STORE_NAME, 'readonly');
        const found = await idbRequest(tx.objectStore(CACHE_STORE_NAME).get(key) as IDBRequest<CachedRead<T>>);
        return found ?? undefined;
      } catch {
        return undefined;
      }
    },
    async put<T>(key: string, value: T) {
      try {
        const db = await open();
        const tx = db.transaction(CACHE_STORE_NAME, 'readwrite');
        tx.objectStore(CACHE_STORE_NAME).put({ value, cachedAt: Date.now() } satisfies CachedRead<T>, key);
        await idbTransaction(tx);
      } catch {
        // A cache that cannot be written is a cache miss next time. Nothing more.
      }
    },
    async clear() {
      try {
        const db = await open();
        const tx = db.transaction(CACHE_STORE_NAME, 'readwrite');
        tx.objectStore(CACHE_STORE_NAME).clear();
        await idbTransaction(tx);
      } catch {
        // Same reasoning.
      }
    },
  };
}

export type CachedOutcome<T> =
  | { ok: true; data: T; /** Epoch ms, present ONLY when served from the cache. */ staleSince?: number }
  | { ok: false; failure: CallFailure };

/**
 * Read through the cache: ask the server, remember the answer, and fall back to what was
 * remembered when the network is gone.
 *
 * STALE IS SERVED ONLY FOR A TRANSPORT FAILURE, and that restriction is a security
 * property rather than tidiness. `call()` distinguishes `unavailable` (no network) from
 * `unauthenticated` / `stale-session` / `error`. Serving a cached answer when the session
 * died would show a person rows their role may no longer read — RLS is enforced at the
 * server, so a cache hit bypasses it entirely. An expired session must produce the
 * ordinary sign-in path, never yesterday's catalogue.
 *
 * A CACHE WRITE FAILURE IS NEVER FATAL: `put` swallows its own errors, so the caller gets
 * the server's answer regardless. The cache is an optimisation, and an optimisation that
 * can fail a request is a defect.
 */
export async function cachedRead<T>(
  cache: ReadCache,
  key: string,
  run: () => Promise<{ ok: true; data: T } | { ok: false; failure: CallFailure }>,
): Promise<CachedOutcome<T>> {
  const result = await run();
  if (result.ok) {
    // GUARDED HERE, not only inside the store. The IndexedDB implementation swallows its
    // own failures, but this function takes any `ReadCache` — and an earlier draft let a
    // rejecting `put` reject the whole read, which contradicted the sentence above it.
    // Caught by its own test, which asserted the contradiction rather than the principle.
    try {
      await cache.put(key, result.data);
    } catch {
      // A cache that cannot be written is a cache miss next time. The caller still gets
      // the server's answer, because an optimisation that can fail a request is a defect.
    }
    return { ok: true, data: result.data };
  }
  if (result.failure.kind !== 'unavailable') return { ok: false, failure: result.failure };

  const remembered = await cache.get<T>(key);
  if (!remembered) return { ok: false, failure: result.failure };
  return { ok: true, data: remembered.value, staleSince: remembered.cachedAt };
}

/** The app-wide cache. Screens import this; tests build their own in memory. */
export const readCache: ReadCache = createIndexedDbReadCache();
